import { ClockCircleTwoTone, InfoCircleTwoTone } from "@ant-design/icons";
import { Alert, Divider, Image, message, Space, Typography } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import rehypeSlug from "rehype-slug";
import rehypeMathjax from "rehype-mathjax";
import styled from "@emotion/styled";
import _ from "lodash";
import theme from "../common/theme";
import { Link } from "react-router-dom";
import { atomOneLight, CopyBlock } from "react-code-blocks";
import { ButtonMailto, IconFont } from "../common/components";
import { filterData } from "../common/utils";
import Mermaid from "../common/mermaid";
import Rate from "./rate";

const { Title } = Typography;

const PostContent = ({ post }) => {
  const {
    title,
    modifiedDate,
    products,
    components,
    versions,
    mdContent,
    relatedPosts,
  } = post;

  // 该内容不适合展示给外部，需要去除
  [products, components, versions].forEach((data) => {
    filterData(data);
  });

  const Subtitle = () => {
    return (
      <Space size={"large"} wrap>
        <Space size={"small"}>
          <ClockCircleTwoTone />
          <span>更新日期：{modifiedDate}</span>
        </Space>

        {!_.isEmpty(products) && (
          <Space size={"small"}>
            <IconFont
              type={"icon-product"}
              style={{ color: `${theme.primary}`, fontSize: "18px" }}
            />
            <span>产品：{products.join("/")}</span>
          </Space>
        )}

        {!_.isEmpty(components) && (
          <Space size={"small"}>
            <IconFont
              type={"icon-component"}
              style={{ color: `${theme.primary}`, fontSize: "18px" }}
            />
            <span>组件：{components.join("/")}</span>
          </Space>
        )}

        {!_.isEmpty(versions) && (
          <Space size={"small"}>
            <InfoCircleTwoTone />
            <span>版本：{versions.join("/")}</span>
          </Space>
        )}
      </Space>
    );
  };

  const MailInfo = () => {
    return (
      <>
        如对文章内容有任何疑问，请联系
        <ButtonMailto
          label={"星环售后"}
          mailto={"mailto:service@transwarp.io"}
        />
        {"<service@transwarp.io>"}
      </>
    );
  };

  return (
    <>
      <Title>{title}</Title>
      <Divider />
      <Subtitle />
      <MdContainer>
        <Divider />
        <Image.PreviewGroup>
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks, remarkMath]}
            // plugins 的顺序有影响，rehypeAutolinkHeadings 要放在 rehypeSlug 后面
            rehypePlugins={[
              rehypeRaw,
              rehypeSlug,
              rehypeAutolinkHeadings,
              rehypeMathjax,
            ]}
            children={mdContent}
            // 链接使用新标签页打开
            linkTarget={(href, children, title) => (
              <Link to={href} target="_blank">
                {title}
              </Link>
            )}
            components={{
              // 代码高亮
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                // 如果没有指定语言，则设为 text
                const rawLanguage = match ? match[1] : "text";
                // 目前无法解析 xml ，可用 markup 替代。参考：https://github.com/rajinwonderland/react-code-blocks/issues/43
                const language = rawLanguage === "xml" ? "markup" : rawLanguage;
                // 渲染流程图
                if (language === "mermaid") {
                  return (
                    <Mermaid className={className} {...props} name="diagram">
                      {children}
                    </Mermaid>
                  );
                } else {
                  return !inline ? (
                    <div style={{ fontSize: "14px" }}>
                      <CopyBlock
                        text={String(children).replace(/\n$|\\/g, "")}
                        language={language}
                        showLineNumbers={false}
                        theme={atomOneLight}
                        codeBlock
                        onCopy={() => {
                          message.success("复制代码成功！");
                        }}
                      />
                    </div>
                  ) : (
                    <code className={className} {...props}>
                      {String(children).replace(/\\/g, "")}
                    </code>
                  );
                }
              },
              // 使用 antd 的组件来渲染标题,并添加标识性的className，以便生成 TOC
              h1: ({ ...props }) => (
                <Title className={"toc-anchor"} level={1} {...props} />
              ),
              h2: ({ ...props }) => (
                <Title className={"toc-anchor"} level={2} {...props} />
              ),
              h3: ({ ...props }) => (
                <Title className={"toc-anchor"} level={3} {...props} />
              ),
              h4: ({ ...props }) => (
                <Title className={"toc-anchor"} level={4} {...props} />
              ),
              h5: ({ ...props }) => (
                <Title className={"toc-anchor"} level={5} {...props} />
              ),
              hr: () => <Divider />,
              // 使用 antd 的组件来渲染图片
              img: ({ src }) => <Image src={src} />,
            }}
          />
        </Image.PreviewGroup>

        {!_.isEmpty(relatedPosts) && (
          <>
            <Divider />
            <Title level={3}> 相关文章 </Title>
            <ul>
              {relatedPosts.map((post) => {
                return (
                  <li>
                    <Link to={post.path} target="_blank">
                      {post.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {/*评分区*/}
        <Rate post={post} />

        <Divider />
        <Alert message={<MailInfo />} type="info" showIcon />
      </MdContainer>
    </>
  );
};

const MdContainer = styled.div`
  min-height: 65vh;

  table {
    border-collapse: collapse;
    margin: 25px 0;
    font-size: 0.9em;
    font-family: sans-serif;
    min-width: 400px;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
  }

  table thead {
    background-color: #333;
    color: white;
    text-align: left;
  }

  table th,
  td {
    padding: 12px 15px;
  }

  table tbody tr {
    border-bottom: 1px solid #dddddd;

    &:hover {
      font-weight: bold;
    }
  }

  table tbody tr:nth-of-type(even) {
    background-color: #f3f3f3;
  }

  //Inline code
  p > code {
    padding: 0.1em 0.6em;
    border-radius: 0.2em;
    white-space: normal;
    background: #ece5f1;
    color: #4b2043;
  }

  //代码块添加圆角
  pre > div {
    border-radius: 0.6em;
  }

  //引用块样式
  blockquote {
    background: #f9f9f9;
    border-left: 10px solid #ccc;
    margin: 1.5em 10px;
    padding: 0.5em 10px;
  }

  blockquote p {
    display: inline;
  }

  ul li {
    list-style-type: disc;
  }

  h2,
  h3 {
    margin-top: 1em;
  }

  h6 {
    margin-bottom: 0.5em;
    color: #000000d9;
    font-weight: 600;
    font-size: 16px;
    line-height: 1.5;
  }
`;

export default PostContent;
